import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ReferenceLine, CartesianGrid } from "recharts";
import { CalendarDays, Plus, Pencil, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  game_date: new Date().toISOString().slice(0, 10),
  opponent: "",
  result: "",
  grade: 7.0,
  notes_md: "",
};

function gradeColor(g) {
  if (g >= 7.5) return "#10B981";  // emerald
  if (g >= 6.0) return "#22D3EE";  // cyan
  if (g >= 4.5) return "#F59E0B";  // amber
  return "#F43F5E";                // rose
}

export function GameGradesCard({ playerId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!playerId) return;
    setLoading(true);
    try {
      const r = await api.get(`/players/${playerId}/game-grades`);
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [playerId]);

  const startCreate = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it }); setOpen(true); };
  const remove = async (it) => {
    if (!window.confirm(`Delete grade for ${it.opponent} (${it.game_date})?`)) return;
    await api.delete(`/game-grades/${it.id}`);
    toast.success("Grade deleted");
    load();
  };

  const avg = items.length
    ? (items.reduce((s, x) => s + (x.grade || 0), 0) / items.length)
    : 0;

  const chartData = items.map((x) => ({
    date: x.game_date,
    label: x.opponent?.slice(0, 12) || x.game_date,
    grade: Number(x.grade) || 0,
  }));

  return (
    <GlassCard className="col-span-12 p-6" testId="eval-game-grades-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-cyan-300" />
            <h3 className="text-1 font-semibold">Game Grades</h3>
            <StatusPill tone="neutral">{items.length} games</StatusPill>
            {items.length > 0 && <StatusPill tone="cyan">Avg {avg.toFixed(1)}</StatusPill>}
          </div>
          <p className="text-3 text-xs mt-1">Per-game grades logged through the season. Watch the trend line to spot rising/falling play.</p>
        </div>
        {isAdmin && (
          <Button data-testid="game-grade-add" onClick={startCreate} className="cta-primary">
            <Plus className="h-4 w-4" /> Log Game
          </Button>
        )}
      </div>

      {/* Chart */}
      {items.length > 0 && (
        <div className="mt-5 h-56 min-h-[220px] w-full min-w-[200px]" data-testid="game-grades-chart">
          <ResponsiveContainer width="100%" height="100%" minHeight={220} minWidth={200}>
            <LineChart data={chartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 9]} ticks={[0, 3, 5, 7, 9]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} />
              <ReferenceLine y={avg} stroke="#22D3EE" strokeDasharray="4 4" label={{ value: `Avg ${avg.toFixed(1)}`, fill: "#22D3EE", fontSize: 10, position: "right" }} />
              <RTooltip
                contentStyle={{ background: "rgba(10,18,28,0.92)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "white", fontSize: 12 }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
              />
              <Line
                type="monotone"
                dataKey="grade"
                stroke="#22D3EE"
                strokeWidth={2.5}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  return <circle key={`d-${cx}-${cy}`} cx={cx} cy={cy} r={4} fill={gradeColor(payload.grade)} stroke="#0a121c" strokeWidth={1.5} />;
                }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* List */}
      <div className="mt-5 space-y-2">
        {loading ? (
          <div className="text-3 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 p-6 text-center">
            <CalendarDays className="h-5 w-5 text-cyan-300 mx-auto mb-1.5" />
            <div className="text-2 text-sm">No game grades logged yet.{isAdmin ? " Click 'Log Game' to add your first one." : ""}</div>
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="glass p-3 flex items-start gap-4 group" data-testid={`game-grade-row-${it.id}`}>
              <div className="shrink-0 w-28">
                <div className="text-3 text-[11px] uppercase tracking-wider">{it.game_date}</div>
                <div className="text-1 text-sm font-medium">{it.opponent}</div>
                {it.result && <div className="text-2 text-xs mt-0.5">{it.result}</div>}
              </div>
              <div className="shrink-0 w-20">
                <div className="text-3 text-[10px] uppercase tracking-wider">Grade</div>
                <div className="text-1 font-mono text-2xl leading-none mt-0.5" style={{ color: gradeColor(it.grade) }}>
                  {Number(it.grade).toFixed(1)}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                {it.notes_md ? (
                  <p className="text-2 text-sm whitespace-pre-wrap leading-relaxed">{it.notes_md}</p>
                ) : (
                  <span className="text-3 text-xs italic">No notes</span>
                )}
              </div>
              {isAdmin && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => startEdit(it)} data-testid={`game-grade-edit-${it.id}`} className="text-2 hover:text-cyan-300 p-1"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(it)} data-testid={`game-grade-delete-${it.id}`} className="text-rose-300/70 hover:text-rose-300 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {open && (
        <GameGradeDialog
          open={open}
          onOpenChange={setOpen}
          playerId={playerId}
          grade={editing}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}
    </GlassCard>
  );
}

function GameGradeDialog({ open, onOpenChange, playerId, grade, onSaved }) {
  const [form, setForm] = useState(grade);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(grade?.id);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.opponent.trim() || !form.game_date) {
      toast.error("Game date and opponent are required");
      return;
    }
    const g = Number(form.grade);
    if (isNaN(g) || g < 0 || g > 9) {
      toast.error("Grade must be between 0 and 9");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        game_date: form.game_date,
        opponent: form.opponent.trim(),
        result: form.result?.trim() || null,
        grade: g,
        notes_md: form.notes_md?.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/game-grades/${grade.id}`, payload);
        toast.success("Grade updated");
      } else {
        await api.post(`/players/${playerId}/game-grades`, payload);
        toast.success("Grade logged");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="game-grade-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isEdit ? "Edit game grade" : "Log game grade"}</DialogTitle>
          <DialogDescription className="text-3 text-xs">Track per-game performance to visualize trends.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Game date</Label>
              <Input data-testid="game-grade-field-date" type="date" value={form.game_date} onChange={(e) => set("game_date", e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Result (optional)</Label>
              <Input data-testid="game-grade-field-result" value={form.result || ""} onChange={(e) => set("result", e.target.value)} placeholder="W 27-14" className="bg-white/5 border-white/10 text-1" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Opponent</Label>
            <Input data-testid="game-grade-field-opponent" value={form.opponent} onChange={(e) => set("opponent", e.target.value)} placeholder="vs. Oregon" className="bg-white/5 border-white/10 text-1" />
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Grade ({Number(form.grade).toFixed(1)} / 9.0)</Label>
            <Input data-testid="game-grade-field-grade" type="range" min="0" max="9" step="0.1" value={form.grade} onChange={(e) => set("grade", parseFloat(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Notes (optional)</Label>
            <Textarea data-testid="game-grade-field-notes" rows={3} value={form.notes_md || ""} onChange={(e) => set("notes_md", e.target.value)} placeholder="Key plays, execution under pressure, red flags…" className="bg-white/5 border-white/10 text-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="game-grade-save" onClick={save} disabled={saving} className="cta-primary">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
